import { useMemo } from "react";
import { Box, Tooltip } from "@mui/material";
import {
  AnnotatedTokens,
  parseAnnotated,
  serializeTokens,
} from "./DirectiveAnnotation";

// #216 — annotation tool for the scoring (judge) view. Reuses the
// per-word click-cycle annotation from DirectiveAnnotation (R1-UI-014)
// — plain → bold → underline → circled → highlight → plain — but adds
// pipe-aware line handling (#214) so multi-line test cells (e.g. "A|X")
// stay stacked while each word remains individually annotatable.
//
// The persisted form is the same markdown subset used for directives
// (**bold**, <u>underline</u>, <span class="circled">…</span>,
// <mark>…</mark> for highlight, #687), with the pipe character separating
// lines. Stored on the movement score as `annotatedDescription`.
// Annotations persist with the score and are scoped to this judge view.

const STATES = ["plain", "bold", "underline", "inverse", "highlight"];
const nextState = (s) => STATES[(STATES.indexOf(s) + 1) % STATES.length];

export default function AnnotatableText({
  plain,
  annotated,
  disabled,
  onChange,
  sx,
}) {
  // Lines come from the pipe-delimited source; each line is annotated
  // independently and rejoined with "|" so pipe line breaks survive.
  const lines = useMemo(() => {
    const source = annotated && annotated.length > 0 ? annotated : plain ?? "";
    return source.split("|").map((line) => parseAnnotated(line.trim()));
  }, [plain, annotated]);

  if (lines.length === 0 || (lines.length === 1 && lines[0].length === 0)) {
    return null;
  }

  const commit = (lineIdx, tokenIdx) => {
    if (disabled) return;
    const nextLines = lines.map((toks, li) =>
      li === lineIdx
        ? toks.map((tok, ti) =>
            ti === tokenIdx ? { ...tok, state: nextState(tok.state) } : tok,
          )
        : toks,
    );
    onChange?.(nextLines.map((toks) => serializeTokens(toks)).join("|"));
  };

  // #2079 — range-select toolbar applies one mark to a whole span of words
  // within a single line (drag-select never crosses the pipe-delimited line
  // boundary, since each line renders its own AnnotatedTokens container).
  const commitRange = (lineIdx, startIdx, endIdx, state) => {
    if (disabled) return;
    const nextLines = lines.map((toks, li) =>
      li === lineIdx
        ? toks.map((tok, ti) =>
            ti >= startIdx && ti <= endIdx ? { ...tok, state } : tok,
          )
        : toks,
    );
    onChange?.(nextLines.map((toks) => serializeTokens(toks)).join("|"));
  };

  const content = (
    // #221 — match the directives (DirectiveAnnotation) font size.
    <Box component="div" sx={{ fontSize: "0.75rem", ...sx }}>
      {lines.map((toks, li) => (
        <Box component="span" key={li} sx={{ display: "block" }}>
          {/* #308 — AnnotatedTokens groups adjacent same-state words into
              one contiguous selection; each word stays individually
              clickable. */}
          <AnnotatedTokens
            tokens={toks}
            disabled={disabled}
            onTokenClick={(ti) => commit(li, ti)}
            onRangeApply={
              disabled
                ? undefined
                : (startIdx, endIdx, state) =>
                    commitRange(li, startIdx, endIdx, state)
            }
          />
        </Box>
      ))}
    </Box>
  );

  if (disabled) return content;
  return (
    <Tooltip
      title="Tap a word to cycle: bold → underline → circle → highlight, or drag across words to mark them all at once"
      arrow
    >
      {content}
    </Tooltip>
  );
}
